'use client';

import clsx from 'clsx';
import TemplateCard from 'components/Cards/TemplateCard/TemplateCard';
import CtaLink from 'components/CtaLink';
import typography from 'components/typography';
import {
  TemplateTemplateFragment,
  TemplateTemplateListingFragment
} from 'contentful/gql/graphql';
import { analytics } from "lib/segment";
import RenderMarkdown from 'molecules/RenderMarkdown/RenderMarkdown';
import { useEffect } from 'react';
import { cn } from 'utils/functions';
import IconList from './IconList';

const TemplateDetail = (props: {
  templateData: TemplateTemplateFragment;
  relatedTemplates?: (TemplateTemplateListingFragment | null)[] | undefined;
}) => {
  const { sys, title, templateLink, embedUrl, body, integrationsCollection, widgetsCollection } = props.templateData;
  useEffect(() => {
    let viewPageEvent = setTimeout(() => {
      analytics?.track('Viewed Template', {
        template_name: title,
        template_id: sys.id,
      });

      return () => {
        clearTimeout(viewPageEvent);
      };
    }, 3000);
  });

  return (
    <div className="bg-primary-neutral-50">
      <main className="container mx-auto py-sm">
        <section id="template-header">
          <div>
            <div className="mb-md flex flex-col items-center justify-between gap-md md:mt-sm md:flex-row">
              <h1 className={cn(typography.heading.longHeadline, "!mb-0")}>{title}</h1>
              {templateLink && (
                <div className="my-md flex justify-end">
                  <CtaLink
                    link={templateLink}
                    variant="Primary"
                    label="Use this template"
                    size="Medium"
                    openInNewWindow={true}
                  />
                </div>
              )}
            </div>
            {embedUrl && (
              <div className="relative hidden overflow-hidden rounded-2xl sm:block">
                {/* TODO: */}
                {/* <Image src={browserImg} width={2000} height={105} alt="" /> */}
                <div className="overflow-hidden">
                  <iframe title={title || ""} src={embedUrl} className="h-[700px] w-full" />
                </div>
              </div>
            )}
          </div>
        </section>
        <section id={sys.id} className="my-md">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="mt-md lg:col-span-1">
              <div className="w-100 h-fit lg:sticky lg:top-3xl">
                {!!integrationsCollection?.items?.length && (
                  <div className="space-y-xs">
                    <h3 className={typography.heading.secondaryComponentHeading}>Integrations</h3>
                    <IconList list={integrationsCollection.items} />
                  </div>
                )}
                {!!widgetsCollection?.items?.length && (
                  <div className="mt-md space-y-xs">
                    <h3 className={typography.heading.secondaryComponentHeading}>Widgets</h3>
                    <IconList list={widgetsCollection.items} />
                  </div>
                )}
                {templateLink && (
                  <div className="my-md flex justify-start">
                    <CtaLink
                      link={templateLink}
                      variant="Primary"
                      label="Use this template"
                      size="Medium"
                      openInNewWindow={true}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className={clsx("col-span-2 w-full whitespace-normal", typography.body.longFormContent)}>
              {body && <RenderMarkdown markdown={body ?? ""} />}
            </div>
          </div>
        </section>
        {props.relatedTemplates && props.relatedTemplates?.length > 0 && (
          <section className="mb-lg mt-lg">
            <div className="relative mx-auto">
              <h2 className={cn(typography.heading.componentHeading, "mb-md text-center")}>Related Templates</h2>
              <div className="grid gap-sm md:grid-cols-3">
                {props.relatedTemplates.map((template) => {
                  return template && <TemplateCard template={template} key={template.sys.id} />;
                })}
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default TemplateDetail;
